import { Modal, Select, Spin } from "antd";
import { Flex } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import "./VideoPreview.scss";

import {
  BsFillCameraVideoFill,
  BsFillCameraVideoOffFill,
  BsFillMicFill,
  BsFillMicMuteFill,
} from "react-icons/bs";

import PrimaryButton from "../../components/UI/PrimaryButton";
import SecondaryButton from "../../components/UI/SecondaryButton";

const VideoPreview = ({
  isOpen,
  onClose,
  zoomClient,
  onConfirm,
  isVideoEnabled,
  isAudioEnabled,
  setIsVideoEnabled,
  setIsAudioEnabled,
}) => {
  const videoRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState(null);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState(null);
  const [hasPermissions, setHasPermissions] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const [stream, setStream] = useState(null);

  const checkPermissionStatus = async () => {
    try {
      const cameraPermission = await navigator.permissions.query({
        name: "camera",
      });
      const micPermission = await navigator.permissions.query({
        name: "microphone",
      });

      if (
        cameraPermission.state === "denied" ||
        micPermission.state === "denied"
      ) {
        setPermissionBlocked(true);
        setPermissionDenied(false);
      } else if (
        cameraPermission.state === "granted" &&
        micPermission.state === "granted"
      ) {
        setHasPermissions(true);
        setPermissionDenied(false);
        setPermissionBlocked(false);
      }
    } catch (error) {
      console.log("Permissions API not supported");
    }
  };

  useEffect(() => {
    if (isOpen) {
      checkPermissionStatus().then(() => {
        if (!permissionBlocked) {
          initializeDevices();
        }
      });
    } else {
      // Make sure we reset loading state when modal is closed
      setLoading(false);
      // Clean up streams
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [isOpen]);

  const initializeDevices = async () => {
    try {
      setLoading(true);
      setPermissionDenied(false);
      setPermissionBlocked(false);

      // Check if we have permission first
      const stream = await navigator.mediaDevices
        .getUserMedia({ video: true, audio: true })
        .catch((err) => {
          setLoading(false);
          setHasPermissions(false);

          // Check the type of error to determine if permissions are blocked
          if (err.name === "NotAllowedError") {
            // Check if permissions are permanently denied
            navigator.permissions
              .query({ name: "camera" })
              .then((cameraPermission) => {
                navigator.permissions
                  .query({ name: "microphone" })
                  .then((micPermission) => {
                    if (
                      cameraPermission.state === "denied" ||
                      micPermission.state === "denied"
                    ) {
                      setPermissionBlocked(true);
                    } else {
                      setPermissionDenied(true);
                    }
                  });
              })
              .catch(() => {
                // Fallback if permissions API is not supported
                setPermissionDenied(true);
              });
          } else {
            setPermissionDenied(true);
          }
          return null;
        });

      if (!stream) return; // Exit if no permissions

      setHasPermissions(true);

      // Stop this temporary stream
      stream.getTracks().forEach((track) => track.stop());

      // Get available devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      const videoInputs = devices.filter(
        (device) => device.kind === "videoinput"
      );
      const audioInputs = devices.filter(
        (device) => device.kind === "audioinput"
      );

      setVideoDevices(
        videoInputs.map((device) => ({
          label: device.label || `Camera ${videoInputs.indexOf(device) + 1}`,
          value: device.deviceId,
        }))
      );

      setAudioDevices(
        audioInputs.map((device) => ({
          label:
            device.label || `Microphone ${audioInputs.indexOf(device) + 1}`,
          value: device.deviceId,
        }))
      );

      // Select first devices as default
      const defaultVideoDevice =
        videoInputs.length > 0 ? videoInputs[0].deviceId : null;
      const defaultAudioDevice =
        audioInputs.length > 0 ? audioInputs[0].deviceId : null;

      setSelectedVideoDevice(defaultVideoDevice);
      setSelectedAudioDevice(defaultAudioDevice);

      // Start preview with default devices
      if (defaultVideoDevice) {
        startPreview(defaultVideoDevice, defaultAudioDevice);
      }
    } catch (error) {
      console.error("Error initializing devices:", error);
      setLoading(false);
      setHasPermissions(false);
      setPermissionDenied(true);
    }
  };

  const startPreview = async (videoDeviceId, audioDeviceId) => {
    try {
      // Stop previous stream if exists
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      // Get new stream with selected devices
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
        audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
      });

      setStream(newStream);

      // Connect stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current
            ?.play()
            ?.catch((e) => console.error("Error playing video:", e));
          setLoading(false);
        };
      }

      setLoading(false);
    } catch (error) {
      console.error("Error starting preview:", error);
      setLoading(false);
    }
  };

  const handleVideoDeviceChange = (deviceId) => {
    setSelectedVideoDevice(deviceId);
    startPreview(deviceId, selectedAudioDevice);
  };

  const handleAudioDeviceChange = (deviceId) => {
    setSelectedAudioDevice(deviceId);
    startPreview(selectedVideoDevice, deviceId);
  };

  const toggleVideo = () => {
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !isVideoEnabled;
        setIsVideoEnabled(!isVideoEnabled);
      }
    }
  };

  const toggleAudio = () => {
    if (stream) {
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !isAudioEnabled;
        setIsAudioEnabled(!isAudioEnabled);
      }
    }
  };

  const handleConfirm = () => {
    if (!hasPermissions) {
      // Re-initialize devices to prompt for permissions again
      initializeDevices();

      return;
    }

    // If we're using the zoomClient SDK, we could update settings here
    // For example: zoomClient.setVideoDevice(selectedVideoDevice);
    if (zoomClient && typeof zoomClient.setVideoDevice === "function") {
      zoomClient.setVideoDevice(selectedVideoDevice);
    }

    if (zoomClient && typeof zoomClient.setAudioDevice === "function") {
      zoomClient.setAudioDevice(selectedAudioDevice);
    }

    // Cleanup and close
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    // onClose();

    onConfirm();
  };

  // Update the onClose handler
  const handleCancel = () => {
    // cleanupStreams();

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    onClose();
  };

  return (
    <Modal
      title="Camera Preview"
      open={isOpen}
      onCancel={handleCancel}
      width={800}
      footer={null}
      destroyOnClose={true}
      centered
      className="videoPreviewModal"
    >
      <div className="previewContainer">
        <Spin spinning={loading}>
          <div className="videoContainer">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`previewVideo ${
                !isVideoEnabled ? "videoDisabled" : ""
              }`}
            />

            {!isVideoEnabled && (
              <div className="videoDisabledOverlay">
                <span style={{ color: "white", marginTop: 8 }}>
                  Camera is turned off
                </span>
              </div>
            )}
          </div>
        </Spin>

        <div className="deviceControls">
          <div className="deviceSelectors">
            <div className="deviceSelector">
              <label>Camera:</label>
              <Select
                value={selectedVideoDevice}
                onChange={handleVideoDeviceChange}
                options={videoDevices}
                style={{ width: "100%" }}
                disabled={loading}
              />
            </div>
            <div className="deviceSelector">
              <label>Microphone:</label>
              <Select
                value={selectedAudioDevice}
                onChange={handleAudioDeviceChange}
                options={audioDevices}
                style={{ width: "100%" }}
                disabled={loading}
              />
            </div>
          </div>

          {!hasPermissions && !loading && (
            <div
              className="permissions-error"
              style={{
                backgroundColor: permissionBlocked ? "#fff1f0" : "#fff2f0",
                border: `1px solid ${
                  permissionBlocked ? "#ff7875" : "#ffcbc2"
                }`,
              }}
            >
              {permissionBlocked ? (
                <div>
                  <span
                    style={{
                      color: "#cf1322",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    Camera and microphone access is blocked
                  </span>
                  <span style={{ color: "#8c8c8c", fontSize: "14px" }}>
                    Please allow camera and microphone access in both your
                    system settings and browser. You may also need to click the
                    camera/microphone icon in the address bar to give
                    permission. Then refresh the page.
                  </span>
                </div>
              ) : (
                <span style={{ color: "#cf1322" }}>
                  Please grant camera and microphone permissions to continue
                </span>
              )}
            </div>
          )}

          <Flex className="controlButtons" justify="center" gap={16}>
            <PrimaryButton onClick={toggleVideo} disabled={loading}>
              {isVideoEnabled ? (
                <BsFillCameraVideoFill style={{ fontSize: "26px" }} />
              ) : (
                <BsFillCameraVideoOffFill style={{ fontSize: "26px" }} />
              )}
            </PrimaryButton>

            <PrimaryButton onClick={toggleAudio} disabled={loading}>
              {isAudioEnabled ? (
                <BsFillMicFill style={{ fontSize: "26px" }} />
              ) : (
                <BsFillMicMuteFill style={{ fontSize: "26px" }} />
              )}
            </PrimaryButton>
          </Flex>
        </div>

        <Flex justify="space-between" className="actionButtons">
          <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>

          <PrimaryButton
            type="primary"
            onClick={handleConfirm}
            disabled={loading || permissionBlocked}
          >
            {permissionBlocked
              ? "Permissions Blocked"
              : !hasPermissions
              ? "Grant Permissions"
              : "Join Waiting Room"}
          </PrimaryButton>
        </Flex>
      </div>
    </Modal>
  );
};

export default VideoPreview;
